import React from 'react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Plus } from 'lucide-react';
import {
  claimFilingCodeOptions,
  claimFrequencyOptions,
  placeOfServiceOptions,
} from '../constants';
import { ADMISSION_TYPE_VALUE_BY_LABEL } from '../../../hooks/useCreatePatientForm/constants';
import DiagnosisCodeList from './DiagnosisCodeList';
import AttachmentUploadCard from './AttachmentUploadCard';
import type { ClaimInformationFormProps } from '../types';

const ClaimInformationForm: React.FC<ClaimInformationFormProps> = ({
  claimInformation,
  onChange,
  onClaimDateChange,
  onDiagnosisChange,
  onOtherDiagnosisChange,
  onAddOtherDiagnosis,
  onRemoveOtherDiagnosis,
  onClaimCodeChange,
  onAddClaimNote,
  attachments,
  onAttachmentUpload,
  onAttachmentRemove,
  selectedPatientId,
}) => {
  // Get claim-level attachments
  const claimLevelAttachments = attachments
    ? Array.from(attachments.values()).filter(
        a => a.serviceLineNumber === undefined
      )
    : [];
  return (
    <div className='space-y-6'>
      {/* Basic Claim Information */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          <label className='block text-sm font-medium mb-1'>
            Claim Charge Amount <span className='text-red-500'>*</span>
          </label>
          <Input
            type='number'
            step='0.01'
            value={claimInformation?.claimChargeAmount || ''}
            onChange={e => onChange('claimChargeAmount', e.target.value)}
            placeholder='Total charge amount'
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium mb-1'>
            Claim Filing Code <span className='text-red-500'>*</span>
          </label>
          <Select
            value={claimInformation?.claimFilingCode || ''}
            onValueChange={value => onChange('claimFilingCode', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select filing code' />
            </SelectTrigger>
            <SelectContent>
              {claimFilingCodeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          <label className='block text-sm font-medium mb-1'>
            Place of Service Code <span className='text-red-500'>*</span>
          </label>
          <Select
            value={claimInformation?.placeOfServiceCode || ''}
            onValueChange={value => onChange('placeOfServiceCode', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select place of service' />
            </SelectTrigger>
            <SelectContent>
              {placeOfServiceOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className='block text-sm font-medium mb-1'>
            Claim Frequency Code <span className='text-red-500'>*</span>
          </label>
          <Select
            value={claimInformation?.claimFrequencyCode || ''}
            onValueChange={value => onChange('claimFrequencyCode', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select frequency' />
            </SelectTrigger>
            <SelectContent>
              {claimFrequencyOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className='block text-sm font-medium mb-1'>
            Patient Control Number
          </label>
          <Input
            value={claimInformation?.patientControlNumber || ''}
            onChange={e => onChange('patientControlNumber', e.target.value)}
            placeholder='Patient control number'
          />
        </div>
      </div>

      {/* Plan and Release Information */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <div>
          <label className='block text-sm font-medium mb-1'>
            Plan Participation Code
          </label>
          <Input
            value={claimInformation?.planParticipationCode || ''}
            onChange={e => onChange('planParticipationCode', e.target.value)}
            placeholder='Plan participation code'
            maxLength={1}
          />
        </div>

        <div>
          <label className='block text-sm font-medium mb-1'>
            Benefits Assignment Certification{' '}
            <span className='text-red-500'>*</span>
          </label>
          <Select
            value={
              claimInformation?.benefitsAssignmentCertificationIndicator || ''
            }
            onValueChange={value =>
              onChange('benefitsAssignmentCertificationIndicator', value)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder='Select Y or N' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='Y'>Y - Yes</SelectItem>
              <SelectItem value='N'>N - No</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className='block text-sm font-medium mb-1'>
            Release Information Code <span className='text-red-500'>*</span>
          </label>
          <Select
            value={claimInformation?.releaseInformationCode || ''}
            onValueChange={value => onChange('releaseInformationCode', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select Y or N' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='Y'>Y - Yes</SelectItem>
              <SelectItem value='N'>N - No</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Claim Date Information */}
      <div>
        <h4 className='text-sm font-semibold mb-3'>Claim Dates</h4>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm font-medium mb-1'>
              Statement Begin Date
            </label>
            <Input
              value={
                claimInformation?.claimDateInformation?.statementBeginDate || ''
              }
              onChange={e =>
                onClaimDateChange('statementBeginDate', e.target.value)
              }
              placeholder='YYYYMMDD'
              maxLength={8}
            />
          </div>

          <div>
            <label className='block text-sm font-medium mb-1'>
              Statement End Date
            </label>
            <Input
              value={
                claimInformation?.claimDateInformation?.statementEndDate || ''
              }
              onChange={e =>
                onClaimDateChange('statementEndDate', e.target.value)
              }
              placeholder='YYYYMMDD'
              maxLength={8}
            />
          </div>

          <div>
            <label className='block text-sm font-medium mb-1'>
              Admission Date
            </label>
            <Input
              value={
                claimInformation?.claimDateInformation?.admissionDateAndHour ||
                ''
              }
              onChange={e =>
                onClaimDateChange('admissionDateAndHour', e.target.value)
              }
              placeholder='YYYYMMDD'
              maxLength={8}
            />
          </div>
        </div>
      </div>

      {/* Diagnosis Information */}
      <div>
        <h4 className='text-sm font-semibold mb-3'>Diagnosis Information</h4>

        {/* Principal Diagnosis */}
        <div className='space-y-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium mb-1'>
                Principal Diagnosis Qualifier Code
              </label>
              <Select
                value={
                  claimInformation?.principalDiagnosis?.qualifierCode || ''
                }
                onValueChange={value =>
                  onDiagnosisChange('principalDiagnosis', {
                    ...claimInformation?.principalDiagnosis,
                    qualifierCode: value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select qualifier' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='BK'>BK - Principal Diagnosis</SelectItem>
                  <SelectItem value='ABK'>
                    ABK - ICD-10-CM Principal Diagnosis
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>
                Principal Diagnosis Code
              </label>
              <Input
                value={
                  claimInformation?.principalDiagnosis
                    ?.principalDiagnosisCode || ''
                }
                onChange={e =>
                  onDiagnosisChange('principalDiagnosis', {
                    ...claimInformation?.principalDiagnosis,
                    principalDiagnosisCode: e.target.value,
                  })
                }
                placeholder='ICD-10 principal diagnosis code'
              />
            </div>
          </div>

          {/* Admitting Diagnosis */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium mb-1'>
                Admitting Diagnosis Qualifier Code
              </label>
              <Select
                value={
                  claimInformation?.admittingDiagnosis?.qualifierCode || ''
                }
                onValueChange={value =>
                  onDiagnosisChange('admittingDiagnosis', {
                    ...claimInformation?.admittingDiagnosis,
                    qualifierCode: value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select qualifier' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='ABJ'>ABJ - Admitting</SelectItem>
                  <SelectItem value='BJ'>BJ - Admitting</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>
                Admitting Diagnosis Code
              </label>
              <Input
                value={
                  claimInformation?.admittingDiagnosis
                    ?.admittingDiagnosisCode || ''
                }
                onChange={e =>
                  onDiagnosisChange('admittingDiagnosis', {
                    ...claimInformation?.admittingDiagnosis,
                    admittingDiagnosisCode: e.target.value,
                  })
                }
                placeholder='ICD-10 admitting diagnosis code'
              />
            </div>
          </div>
        </div>

        {/* Other Diagnosis Codes */}
        <div className='mt-6'>
          <DiagnosisCodeList
            diagnosisList={
              claimInformation?.otherDiagnosisInformationList || []
            }
            onChange={onOtherDiagnosisChange}
            onAdd={onAddOtherDiagnosis}
            onRemove={onRemoveOtherDiagnosis}
            title='Other Diagnosis Codes'
          />
        </div>
      </div>

      {/* Claim Code Information */}
      <div>
        <h4 className='text-sm font-semibold mb-3'>Claim Code Information</h4>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label className='block text-sm font-medium mb-1'>
              Admission Type Code <span className='text-red-500'>*</span>
            </label>
            <Select
              value={
                claimInformation?.claimCodeInformation?.admissionTypeCode || ''
              }
              onValueChange={value =>
                onClaimCodeChange('admissionTypeCode', value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Select admission type' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ADMISSION_TYPE_VALUE_BY_LABEL['Emergency']}>
                  1 - Emergency
                </SelectItem>
                <SelectItem value={ADMISSION_TYPE_VALUE_BY_LABEL['Urgent']}>
                  2 - Urgent
                </SelectItem>
                <SelectItem value={ADMISSION_TYPE_VALUE_BY_LABEL['Elective']}>
                  3 - Elective
                </SelectItem>
                <SelectItem value={ADMISSION_TYPE_VALUE_BY_LABEL['Newborn']}>
                  4 - Newborn
                </SelectItem>
                <SelectItem value={ADMISSION_TYPE_VALUE_BY_LABEL['Trauma']}>
                  5 - Trauma Center
                </SelectItem>
                <SelectItem
                  value={
                    ADMISSION_TYPE_VALUE_BY_LABEL['Information not Available']
                  }
                >
                  9 - Information Not Available
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className='block text-sm font-medium mb-1'>
              Patient Status Code <span className='text-red-500'>*</span>
            </label>
            <Select
              value={
                claimInformation?.claimCodeInformation?.patientStatusCode || ''
              }
              onValueChange={value =>
                onClaimCodeChange('patientStatusCode', value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Select patient status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='01'>01 - Discharged to home</SelectItem>
                <SelectItem value='02'>
                  02 - Discharged/transferred to short-term hospital
                </SelectItem>
                <SelectItem value='03'>
                  03 - Discharged/transferred to SNF
                </SelectItem>
                <SelectItem value='04'>
                  04 - Discharged/transferred to ICF
                </SelectItem>
                <SelectItem value='05'>
                  05 - Discharged/transferred to other facility
                </SelectItem>
                <SelectItem value='06'>
                  06 - Discharged/transferred to home health
                </SelectItem>
                <SelectItem value='07'>
                  07 - Left against medical advice
                </SelectItem>
                <SelectItem value='20'>20 - Expired</SelectItem>
                <SelectItem value='30'>30 - Still patient</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
          {/* Condition Code - Not available in current schema */}
          {/* <div>
            <label className='block text-sm font-medium mb-1'>
              Condition Code
            </label>
            <Input
              value={
                claimInformation?.claimCodeInformation?.conditionCode || ''
              }
              onChange={e => onClaimCodeChange('conditionCode', e.target.value)}
              placeholder='Condition code'
            />
          </div> */}

          {/* Occurrence Code - Not available in current schema */}
          {/* <div>
            <label className='block text-sm font-medium mb-1'>
              Occurrence Code
            </label>
            <Input
              value={
                claimInformation?.claimCodeInformation?.occurrenceCode || ''
              }
              onChange={e =>
                onClaimCodeChange('occurrenceCode', e.target.value)
              }
              placeholder='Occurrence code'
            />
          </div> */}
        </div>
      </div>

      {/* Claim Notes */}
      <div>
        <h4 className='text-sm font-semibold mb-3'>Claim Notes</h4>
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-gray-600'>
              Add notes for this claim
            </span>
            <Button
              type='button'
              onClick={onAddClaimNote}
              size='sm'
              variant='outline'
            >
              <Plus className='w-4 h-4' />
              Add Note
            </Button>
          </div>

          {/* Claim Notes - claimNotes is now an object with goalRehabOrDischargePlans and diagnosisDescription arrays */}
          {/* This section needs refactoring to work with the new claimNotes structure */}
        </div>
      </div>

      {/* Additional Information */}
      <div>
        <h4 className='text-sm font-semibold mb-3'>Additional Information</h4>
        <div className='space-y-3'>
          {/* Onset of Current Illness/Symptom Date - Not available in current schema */}
          {/* Field onsetOfCurrentIllnessOrSymptomDate is not available in Stedi837iClaimInformation */}
        </div>
      </div>

      {/* Claim Attachments */}
      {selectedPatientId && onAttachmentUpload && onAttachmentRemove && (
        <div>
          <h4 className='text-sm font-semibold mb-3'>
            Claim Attachments
            {claimLevelAttachments.length > 0 && (
              <span className='ml-2 text-xs font-normal text-gray-500'>
                ({claimLevelAttachments.length} file
                {claimLevelAttachments.length !== 1 ? 's' : ''})
              </span>
            )}
          </h4>
          <div className='space-y-3'>
            <p className='text-sm text-gray-600'>
              Upload supporting documentation for this claim. These attachments
              will be included with the claim submission.
            </p>

            {/* Display existing attachments */}
            {claimLevelAttachments.map(attachment => (
              <AttachmentUploadCard
                key={attachment.id}
                patientId={selectedPatientId}
                existingAttachment={attachment}
                onUploadSuccess={() => {}}
                onRemove={() => onAttachmentRemove(attachment.id)}
              />
            ))}

            {/* Add new attachment button - limit to 10 attachments */}
            {claimLevelAttachments.length < 10 && (
              <AttachmentUploadCard
                patientId={selectedPatientId}
                onUploadSuccess={onAttachmentUpload}
              />
            )}

            {claimLevelAttachments.length >= 10 && (
              <p className='text-sm text-gray-500 italic'>
                Maximum of 10 attachments reached for claim level.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimInformationForm;
